import { stringCmp } from '~/Utils';

import type { PromotionChange } from './types';

// The two orthogonal scope dimensions a change can be attributed to. The key matches the `scope` field on
// `PromotionChange`; a change can belong to multiple members of each, independently — so the two are filtered
// as separate facets rather than interleaved into one list.
export type ScopeDimension = 'sub_organizations' | 'lobs';

export interface ScopeDimensionMeta {
  key: ScopeDimension;
  // Compact row label shown per change ("Sub-orgs:" / "LOBs:").
  label: string;
  // The facet filter's field label — the same wording the sysconfig rule screens use for these filters
  // (SubOrganizationMultiSelect / LobMultiSelect).
  filterLabel: string;
}

// Sub-orgs first, then LOBs — the order the per-change rows and the two facet filters render in.
export const SCOPE_DIMENSIONS: ScopeDimensionMeta[] = [
  { key: 'sub_organizations', label: 'Sub-orgs', filterLabel: 'Sub Organizations' },
  { key: 'lobs', label: 'LOBs', filterLabel: 'Line of Business' },
];

// Facet-option sentinel for "unscoped" (global) items in a dimension. Selecting it narrows to changes that
// aren't attributed to any specific member of that dimension. A real member value can never collide: members
// are org / LOB identifiers, never this literal.
// Labeled "Global" — deliberately not "All": the facet treats unscoped items as their own bucket (they do
// NOT match a specific-member selection), and "All" would promise match-everything semantics it doesn't have.
export const GLOBAL_SCOPE_VALUE = '__global__';
export const GLOBAL_SCOPE_LABEL = 'Global';

// The change's attributed member values for a dimension. An empty (or absent) array means the change is not
// scoped to specific members of that dimension — it applies globally within it.
export const scopeValuesFor = (change: PromotionChange, dimension: ScopeDimension): string[] =>
  (dimension === 'lobs' ? change.scope?.lobs : change.scope?.sub_organizations) ?? [];

// The member values pruned from this change during scope projection — dropped because they're absent from the
// target. Parallel to `scopeValuesFor`, reading `change.pruned` instead of `change.scope`.
export const prunedValuesFor = (change: PromotionChange, dimension: ScopeDimension): string[] =>
  (dimension === 'lobs' ? change.pruned?.lobs : change.pruned?.sub_organizations) ?? [];

// A change is "global" in a dimension when it names no specific member of it.
export const isGloballyScoped = (change: PromotionChange, dimension: ScopeDimension): boolean =>
  scopeValuesFor(change, dimension).length === 0;

// Distinct member values present across all changes for a dimension, sorted for a stable option order.
export const distinctScopeValues = (changes: PromotionChange[], dimension: ScopeDimension): string[] => {
  const seen = new Set<string>();
  changes.forEach((change) => scopeValuesFor(change, dimension).forEach((value) => seen.add(value)));
  return Array.from(seen).sort(stringCmp);
};

// Whether any change is unscoped in a dimension — controls whether that facet offers the "Global" option.
export const hasGlobalInDimension = (changes: PromotionChange[], dimension: ScopeDimension): boolean =>
  changes.some((change) => isGloballyScoped(change, dimension));

// A change matches one facet's selection (OR within the facet): an empty selection never constrains; a
// non-empty one matches when the change shares a selected real member, or — for an unscoped change — when
// "Global" is selected.
export const matchesFacet = (change: PromotionChange, dimension: ScopeDimension, selected: Set<string>): boolean => {
  if (selected.size === 0) {
    return true;
  }
  const values = scopeValuesFor(change, dimension);
  if (values.length === 0) {
    return selected.has(GLOBAL_SCOPE_VALUE);
  }
  return values.some((value) => selected.has(value));
};

export type ScopeFilterSets = Record<ScopeDimension, Set<string>>;

// A change is visible when it matches every facet (AND across facets).
export const isChangeVisible = (change: PromotionChange, filters: ScopeFilterSets): boolean =>
  SCOPE_DIMENSIONS.every((dimension) => matchesFacet(change, dimension.key, filters[dimension.key]));

// True when any facet has an active selection (⇒ the view is narrowed).
export const isAnyScopeFilterActive = (filters: ScopeFilterSets): boolean =>
  SCOPE_DIMENSIONS.some((dimension) => filters[dimension.key].size > 0);
